import React, { useEffect, useState } from "react";
import {
  FormField,
  ColumnLayout,
  Select,
  Popover,
  Box,
  Button,
  ButtonDropdown,
  ButtonDropdownProps,
  Input,
  DatePicker,
  Checkbox,
} from "@amzn/awsui-components-react/polaris";
import MonetaryInput from "components/monetary-input/MonetaryInput";
import { IOutcomeBreakdownRowProps } from "./definitions";
import styles from "components/assessment-wizard/style.module.scss";
import { addCommasToNumberString } from "components/monetary-input/definitions";
import {
  TaxYearBreakdown,
  TaxAreaBreakdown,
  TaxYearAreaBreakdown,
} from "models/schemas";
import {
  INACTIVE_TAX_AREA_STRING,
  LabelPopovers,
  TableLabels,
} from "../outcome-breakdown-table/constants";
import { toDateString } from "utils/date-format";

const OutcomeBreakdownRow: React.FC<IOutcomeBreakdownRowProps> = ({
  breakdown,
  currencySign,
  isFirstRow,
  setBreakdownInList,
  taxAreaOptions,
  onRemove,
  onDuplicate,
  showRemoveButton,
  disableRemove,
  startYear,
  isEditMode,
}) => {
  const [category, setCategory] = useState<string>(
    breakdown.category === "" ? "Select" : breakdown.category,
  );

  const isTaxArea = breakdown.type === "Tax area";
  const isTaxYearArea = breakdown.type === "Tax year & area";

  const [taxYearBreakdown, setTaxYearBreakdown] = useState<TaxYearBreakdown>(
    isTaxArea || isTaxYearArea
      ? {
          outcomeBreakdownId: "",
          parentId: "",
          totalTaxAmount: "",
          totalPenaltyAmount: "",
          totalInterestAmount: "",
          totalOtherAmount: "",
          type: "Tax year",
          category: "",
        }
      : (breakdown as TaxYearBreakdown),
  );

  const [taxAreaBreakdown, setTaxAreaBreakdown] = useState<TaxAreaBreakdown>(
    isTaxArea && !isTaxYearArea
      ? (breakdown as TaxAreaBreakdown)
      : {
          outcomeBreakdownId: "",
          parentId: "",
          amountReceived: "",
          amountInError: "",
          errorRate: "",
          amountAssessed: "",
          type: "Tax area",
          category: "",
        },
  );

  const [taxYearAreaBreakdown, setTaxYearAreaBreakdown] =
    useState<TaxYearAreaBreakdown>(
      isTaxYearArea
        ? {
            ...(breakdown as TaxYearAreaBreakdown),
            dueDate:
              breakdown.dueDate && !Number.isNaN(Number(breakdown.dueDate))
                ? toDateString(Number(breakdown.dueDate))
                : breakdown.dueDate || "",
          }
        : {
            outcomeBreakdownId: "",
            parentId: "",
            taxYear: "",
            taxAreaId: "",
            totalTaxAmount: "",
            totalPenaltyAmount: "",
            totalInterestAmount: "",
            totalOtherAmount: "",
            dueDate: "",
            additionalInformation: "",
            type: "Tax year & area",
            category: "",
          },
    );

  const generateYearOptions = () => {
    const currentYear = new Date().getUTCFullYear();
    const minYear = (startYear ?? currentYear) - 10;
    const maxYear = currentYear + 2;
    const yearCount = maxYear - minYear + 1;

    return Array.from({ length: yearCount }, (_, i) => {
      const year = minYear + i;
      const yearStr = year.toString();
      return { label: yearStr, value: yearStr };
    });
  };

  const calculateRowTotal = () => {
    if (isTaxArea) {
      const { amountReceived, amountInError, amountAssessed } =
        taxAreaBreakdown;

      const roundedAmountReceived = Number(amountReceived || 0).toFixed(2);
      const roundedAmountInError = Number(amountInError || 0).toFixed(2);
      const roundedAmountAssessed = Number(amountAssessed || 0).toFixed(2);

      return (
        Number(roundedAmountReceived) +
        Number(roundedAmountInError) +
        Number(roundedAmountAssessed)
      ).toFixed(2);
    }
    if (isTaxYearArea) {
      const {
        totalTaxAmount,
        totalPenaltyAmount,
        totalInterestAmount,
        totalOtherAmount,
      } = taxYearAreaBreakdown;

      const roundedTax = Number(totalTaxAmount || 0).toFixed(2);
      const roundedPenalty = Number(totalPenaltyAmount || 0).toFixed(2);
      const roundedInterest = Number(totalInterestAmount || 0).toFixed(2);
      const roundedOther = Number(totalOtherAmount || 0).toFixed(2);

      return (
        Number(roundedTax) +
        Number(roundedPenalty) +
        Number(roundedInterest) +
        Number(roundedOther)
      ).toFixed(2);
    }
    const {
      totalTaxAmount,
      totalPenaltyAmount,
      totalInterestAmount,
      totalOtherAmount,
    } = taxYearBreakdown;

    const roundedTax = Number(totalTaxAmount || 0).toFixed(2);
    const roundedPenalty = Number(totalPenaltyAmount || 0).toFixed(2);
    const roundedInterest = Number(totalInterestAmount || 0).toFixed(2);
    const roundedOther = Number(totalOtherAmount || 0).toFixed(2);

    return (
      Number(roundedTax) +
      Number(roundedPenalty) +
      Number(roundedInterest) +
      Number(roundedOther)
    ).toFixed(2);
  };

  useEffect(() => {
    if (isTaxArea) {
      const updatedBreakdown = {
        ...taxAreaBreakdown,
        category,
      };

      setBreakdownInList(updatedBreakdown);

      const receivedAmount = Number(taxAreaBreakdown.amountReceived);
      const errorAmount = Number(taxAreaBreakdown.amountInError);

      if (receivedAmount > 0 && errorAmount > 0) {
        const calculatedErrorRate = (errorAmount / receivedAmount) * 100;

        setTaxAreaBreakdown((prev) => ({
          ...prev,
          errorRate: calculatedErrorRate.toFixed(2),
        }));
      } else {
        setTaxAreaBreakdown((prev) => ({
          ...prev,
          errorRate: "",
        }));
      }
    } else if (isTaxYearArea) {
      const updatedBreakdown = {
        ...taxYearAreaBreakdown,
        category,
      };

      setBreakdownInList(updatedBreakdown);
    } else {
      const updatedBreakdown = {
        ...taxYearBreakdown,
        category,
      };

      setBreakdownInList(updatedBreakdown);
    }
  }, [
    taxYearBreakdown,
    taxAreaBreakdown.amountAssessed,
    taxAreaBreakdown.amountReceived,
    taxAreaBreakdown.amountInError,
    taxYearAreaBreakdown,
    category,
    isTaxArea,
    isTaxYearArea,
    setBreakdownInList,
  ]);

  if (isTaxYearArea) {
    return (
      <div
        className={
          isEditMode
            ? styles.taxYearAreaGrid
            : styles.taxYearAreaGridNoSuperseded
        }
      >
        <div className={styles.gridItemSelect}>
          <FormField label={isFirstRow ? "Tax year" : ""}>
            <Select
              selectedOption={{
                label: taxYearAreaBreakdown.taxYear || "Select",
                value: taxYearAreaBreakdown.taxYear || "",
              }}
              onChange={({ detail }) =>
                setTaxYearAreaBreakdown((prev) => ({
                  ...prev,
                  taxYear: detail.selectedOption.value || "",
                }))
              }
              options={generateYearOptions()}
              filteringType="auto"
              placeholder="Select year"
            />
          </FormField>
        </div>
        <div className={styles.gridItemSelect}>
          <FormField label={isFirstRow ? "Tax area" : ""}>
            <Select
              selectedOption={{
                label:
                  taxAreaOptions!.find(
                    (option) => option.value === taxYearAreaBreakdown.taxAreaId,
                  )?.label ?? "Select",
                value: taxYearAreaBreakdown.taxAreaId,
              }}
              onChange={({ detail }) =>
                setTaxYearAreaBreakdown((prev) => ({
                  ...prev,
                  taxAreaId: detail.selectedOption.value!,
                }))
              }
              options={taxAreaOptions!.sort((a, b) => {
                return a.label.localeCompare(b.label);
              })}
              filteringType="auto"
            />
          </FormField>
        </div>
        <FormField label={isFirstRow ? "Tax" : ""}>
          <MonetaryInput
            title=""
            value={taxYearAreaBreakdown.totalTaxAmount}
            onChange={(value) =>
              setTaxYearAreaBreakdown((prev) => ({
                ...prev,
                totalTaxAmount: value,
              }))
            }
            currencySign={currencySign}
          />
        </FormField>
        <FormField label={isFirstRow ? "Penalty" : ""}>
          <MonetaryInput
            title=""
            value={taxYearAreaBreakdown.totalPenaltyAmount}
            onChange={(value) =>
              setTaxYearAreaBreakdown((prev) => ({
                ...prev,
                totalPenaltyAmount: value,
              }))
            }
            currencySign={currencySign}
          />
        </FormField>
        <FormField label={isFirstRow ? "Interest" : ""}>
          <MonetaryInput
            title=""
            value={taxYearAreaBreakdown.totalInterestAmount}
            onChange={(value) =>
              setTaxYearAreaBreakdown((prev) => ({
                ...prev,
                totalInterestAmount: value,
              }))
            }
            currencySign={currencySign}
          />
        </FormField>
        <FormField label={isFirstRow ? "Other" : ""}>
          <MonetaryInput
            title=""
            value={taxYearAreaBreakdown.totalOtherAmount}
            onChange={(value) =>
              setTaxYearAreaBreakdown((prev) => ({
                ...prev,
                totalOtherAmount: value,
              }))
            }
            currencySign={currencySign}
          />
        </FormField>
        <FormField label={isFirstRow ? "Due date" : ""}>
          <DatePicker
            value={taxYearAreaBreakdown.dueDate}
            onChange={({ detail }) =>
              setTaxYearAreaBreakdown((prev) => ({
                ...prev,
                dueDate: detail.value,
              }))
            }
            placeholder="YYYY/MM/DD"
          />
        </FormField>
        {isEditMode && (
          <div className={styles.supersededCellWrapper}>
            <FormField label={isFirstRow ? "Superseded" : ""}>
              <Checkbox
                checked={!!taxYearAreaBreakdown.superseded}
                onChange={({ detail }) =>
                  setTaxYearAreaBreakdown((prev) => ({
                    ...prev,
                    superseded: detail.checked,
                  }))
                }
              />
            </FormField>
          </div>
        )}
        <FormField label={isFirstRow ? "Additional information" : ""}>
          <Input
            value={taxYearAreaBreakdown.additionalInformation || ""}
            onChange={({ detail }) =>
              setTaxYearAreaBreakdown((prev) => ({
                ...prev,
                additionalInformation: detail.value,
              }))
            }
            placeholder="Optional"
          />
        </FormField>
        <div className={styles.dollarInput}>
          <FormField label={isFirstRow ? "Total" : ""}>
            <div className={styles.inputContainer}>
              <span className={styles.dollarSign}> {currencySign} </span>
              <input
                value={addCommasToNumberString(calculateRowTotal())}
                readOnly={true}
                className={styles.inputField}
              />
            </div>
          </FormField>
        </div>
        {showRemoveButton && (
          <FormField label={isFirstRow ? <span>&nbsp;</span> : ""}>
            <ButtonDropdown
              items={[
                { id: "duplicate", text: "Duplicate" },
                {
                  id: "remove",
                  text: "Remove",
                  disabled: disableRemove,
                  disabledReason: "There must be at least one breakdown row",
                },
              ]}
              onItemClick={(
                event: CustomEvent<ButtonDropdownProps.ItemClickDetails>,
              ) => {
                if (event.detail.id === "duplicate") {
                  onDuplicate?.(breakdown as TaxYearAreaBreakdown);
                } else if (event.detail.id === "remove") {
                  onRemove?.(breakdown as TaxYearAreaBreakdown);
                }
              }}
              variant="icon"
            />
          </FormField>
        )}
      </div>
    );
  }
  if (!isTaxArea) {
    return (
      <ColumnLayout columns={6} minColumnWidth={1}>
        <FormField label={isFirstRow ? "Tax year" : ""}>
          <div className={styles.taxYearCategoryText}>
            <strong>{taxYearBreakdown.category}</strong>
          </div>
        </FormField>
        <FormField label={isFirstRow ? "Tax" : ""}>
          <MonetaryInput
            title=""
            value={taxYearBreakdown.totalTaxAmount}
            onChange={(value) =>
              setTaxYearBreakdown((prev) => ({
                ...prev,
                totalTaxAmount: value,
              }))
            }
            currencySign={currencySign}
          />
        </FormField>
        <FormField label={isFirstRow ? "Penalty" : ""}>
          <MonetaryInput
            title=""
            value={taxYearBreakdown.totalPenaltyAmount}
            onChange={(value) =>
              setTaxYearBreakdown((prev) => ({
                ...prev,
                totalPenaltyAmount: value,
              }))
            }
            currencySign={currencySign}
          />
        </FormField>
        <FormField label={isFirstRow ? "Interest" : ""}>
          <MonetaryInput
            title=""
            value={taxYearBreakdown.totalInterestAmount}
            onChange={(value) =>
              setTaxYearBreakdown((prev) => ({
                ...prev,
                totalInterestAmount: value,
              }))
            }
            currencySign={currencySign}
          />
        </FormField>
        <FormField label={isFirstRow ? "Other" : ""}>
          <MonetaryInput
            title=""
            value={taxYearBreakdown.totalOtherAmount}
            onChange={(value) =>
              setTaxYearBreakdown((prev) => ({
                ...prev,
                totalOtherAmount: value,
              }))
            }
            currencySign={currencySign}
          />
        </FormField>
        <div className={styles.dollarInput}>
          <FormField label={isFirstRow ? "Total" : ""}>
            <div className={styles.inputContainer}>
              <span className={styles.dollarSign}> {currencySign} </span>
              <input
                value={addCommasToNumberString(calculateRowTotal())}
                readOnly={true}
                className={styles.inputField}
              />
            </div>
          </FormField>
        </div>
      </ColumnLayout>
    );
  }
  return (
    <ColumnLayout columns={6} minColumnWidth={1}>
      <FormField label={isFirstRow ? "Tax area" : ""}>
        <Select
          selectedOption={{
            label:
              taxAreaOptions!.find((option) => option.value === category)
                ?.label ??
              (category === "Select" ? category : INACTIVE_TAX_AREA_STRING),
            value: category,
          }}
          onChange={({ detail }) => setCategory(detail.selectedOption.value!)}
          options={taxAreaOptions!.sort((a, b) => {
            return a.label.localeCompare(b.label);
          })}
          filteringType="auto"
        />
      </FormField>
      <FormField
        label={
          isFirstRow ? (
            <Popover
              dismissButton={false}
              content={<Box>{LabelPopovers.AMOUNT_REVIEWED}</Box>}
            >
              <strong>{TableLabels.AMOUNT_REVIEWED}</strong>
            </Popover>
          ) : (
            ""
          )
        }
      >
        <MonetaryInput
          title=""
          value={taxAreaBreakdown.amountReceived}
          onChange={(value) =>
            setTaxAreaBreakdown((prev) => ({
              ...prev,
              amountReceived: value,
            }))
          }
          currencySign={currencySign}
        />
      </FormField>
      <FormField
        label={
          isFirstRow ? (
            <Popover
              dismissButton={false}
              content={<Box>{LabelPopovers.AMOUNT_IN_ERROR}</Box>}
            >
              <strong>{TableLabels.AMOUNT_IN_ERROR}</strong>
            </Popover>
          ) : (
            ""
          )
        }
      >
        <MonetaryInput
          title=""
          value={taxAreaBreakdown.amountInError}
          onChange={(value) =>
            setTaxAreaBreakdown((prev) => ({
              ...prev,
              amountInError: value,
            }))
          }
          currencySign={currencySign}
        />
      </FormField>
      <div className={styles.percentageInput}>
        <FormField
          label={
            isFirstRow ? (
              <Popover
                dismissButton={false}
                content={
                  <Box>
                    <div>{LabelPopovers.ERROR_RATE}</div>
                  </Box>
                }
              >
                <strong>{TableLabels.ERROR_RATE}</strong>
              </Popover>
            ) : (
              ""
            )
          }
        >
          <div className={styles.inputContainer}>
            <input
              value={
                taxAreaBreakdown.errorRate
                  ? `${taxAreaBreakdown.errorRate}%`
                  : ""
              }
              readOnly={true}
              className={styles.inputField}
            />
          </div>
        </FormField>
      </div>
      <FormField
        label={
          isFirstRow ? (
            <Popover
              dismissButton={false}
              content={<Box>{LabelPopovers.AMOUNT_ASSESSED}</Box>}
            >
              <strong>{TableLabels.AMOUNT_ASSESSED}</strong>
            </Popover>
          ) : (
            ""
          )
        }
      >
        <MonetaryInput
          title=""
          value={taxAreaBreakdown.amountAssessed}
          onChange={(value) =>
            setTaxAreaBreakdown((prev) => ({
              ...prev,
              amountAssessed: value,
            }))
          }
          currencySign={currencySign}
        />
      </FormField>
      {showRemoveButton ? (
        <FormField label={isFirstRow ? <span>&nbsp;</span> : ""}>
          <Button
            onClick={() => {
              onRemove?.(breakdown);
            }}
          >
            Remove
          </Button>
        </FormField>
      ) : (
        <></>
      )}
    </ColumnLayout>
  );
};

export default OutcomeBreakdownRow;
